<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-cart-shopping me-2" style="color: #64748b;"></i>{{ $t('VAT Purchase List') }}
          </h5>
          <small class="text-muted">{{ $t('Input VAT report for PP.30 filing') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-success btn-sm" @click="exportExcel" :disabled="loading || !invoices.length">
          <i class="fa-solid fa-file-excel me-1"></i>{{ $t('Export Excel') }}
        </button>
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- Missing tax_id warning -->
    <div
      v-if="missingTaxIdCount > 0"
      class="alert alert-warning d-flex align-items-center"
      role="alert"
    >
      <i class="fa-solid fa-triangle-exclamation fa-lg me-2"></i>
      <div>
        {{ $t('Some invoices are missing the supplier Tax ID and cannot be used for filing.') }}
        ({{ missingTaxIdCount }})
      </div>
    </div>

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Invoices') }}</div>
            <div class="fw-bold fs-4">{{ summary.total_invoices || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Taxable') }}</div>
            <div class="fw-bold fs-5 text-success">{{ formatCurrency(summary.total_taxable) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <!-- ยอดที่เอาไปเครดิตได้จริง — ไม่ใช่ VAT ทั้งหมดที่จ่ายไป -->
            <div class="text-muted small mb-1">{{ $t('Claimable Input VAT') }}</div>
            <div class="fw-bold fs-5 text-primary">{{ formatCurrency(summary.total_vat_claimable) }}</div>
            <div v-if="toNum(summary.total_vat_non_claimable) > 0" class="text-muted small">
              {{ $t('Not claimable') }}: {{ formatCurrency(summary.total_vat_non_claimable) }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Amount') }}</div>
            <div class="fw-bold fs-5">{{ formatCurrency(summary.total_amount) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Invoice Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3 text-end" style="width: 50px;">#</th>
                <th>{{ $t('Date') }}</th>
                <th>{{ $t('Source') }}</th>
                <th>{{ $t('Tax Invoice No.') }}</th>
                <th>{{ $t('Supplier') }}</th>
                <th class="text-end">{{ $t('Taxable') }}</th>
                <th class="text-end">{{ $t('VAT') }}</th>
                <th class="text-end pe-3">{{ $t('Total Amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!invoices.length && !loading">
                <td colspan="8" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr
                v-for="row in invoices"
                :key="row.seq"
                :class="{ 'table-warning-subtle': !row.supplier_tax_id }"
              >
                <td class="ps-3 text-end">{{ row.seq }}</td>
                <td class="text-nowrap">{{ row.date }}</td>
                <td>
                  <span class="badge" :class="row.source === 'expense' ? 'bg-info' : 'bg-secondary'">
                    {{ $t(sourceLabel(row.source)) }}
                  </span>
                  <div v-if="row.category" class="text-muted small">{{ $t(row.category) }}</div>
                  <div v-else-if="row.document_no" class="text-muted small">{{ row.document_no }}</div>
                </td>
                <td>
                  <span class="fw-semibold">{{ row.tax_invoice_no || '-' }}</span>
                  <div v-if="!row.is_vat_claimable" class="text-warning small">
                    <i class="fa-solid fa-ban me-1"></i>{{ $t('Not claimable') }}
                  </div>
                </td>
                <td>
                  <div>{{ row.supplier_name || '-' }}</div>
                  <div v-if="row.supplier_tax_id" class="text-muted small">
                    {{ row.supplier_tax_id }}
                    <span v-if="row.supplier_branch">({{ row.supplier_branch }})</span>
                  </div>
                  <div v-else class="text-warning small" :title="$t('Tax ID is required before filing.')">
                    <i class="fa-solid fa-triangle-exclamation me-1"></i>{{ $t('Missing Tax ID') }}
                  </div>
                </td>
                <td class="text-end">{{ formatCurrency(row.taxable_amount) }}</td>
                <td class="text-end">{{ formatCurrency(row.vat_amount) }}</td>
                <td class="text-end fw-semibold pe-3">{{ formatCurrency(row.total_amount) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="invoices.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3" colspan="5">{{ $t('Total') }}</td>
                <td class="text-end text-success">{{ formatCurrency(summary.total_taxable) }}</td>
                <td class="text-end text-primary">{{ formatCurrency(summary.total_vat) }}</td>
                <td class="text-end pe-3">{{ formatCurrency(summary.total_amount) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { Reports } from '@/services/endpoint/reports.service';
import { exportRowsToXlsx, MONEY_FMT } from '@/utils/excelExport';
import reportMixin from './reportMixin';

export default {
  name: 'ReportVatPurchaseList',
  mixins: [reportMixin],
  data() {
    // Default to the current calendar month — the typical PP.30 filing period.
    const start = moment().startOf('month').toDate();
    const end = moment().endOf('month').toDate();
    return {
      reportLabel: 'VAT Purchase List',
      filters: {
        branch: 'all',
        date_range: { startDate: start, endDate: end },
        dateFrom: moment(start).format('YYYY-MM-DD'),
        dateTo: moment(end).format('YYYY-MM-DD'),
      },
      summary: {
        total_invoices: 0,
        total_taxable: 0,
        total_vat: 0,
        total_amount: 0,
        total_vat_claimable: 0,
        total_vat_non_claimable: 0,
      },
      invoices: [],
    };
  },
  computed: {
    missingTaxIdCount() {
      return this.invoices.filter(r => !r.supplier_tax_id).length;
    },
  },
  methods: {
    // ที่มาของใบกำกับ — รับของเข้าสต็อก vs ค่าใช้จ่าย (silver-spoon-api/docs/vat-compliance.md Phase 2)
    sourceLabel(source) {
      return source === 'expense' ? 'Expense' : 'Goods Receipt';
    },

    async fetchReport() {
      const res = await Reports.vatPurchaseList(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.summary = payload.summary || this.summary;
      this.invoices = payload.invoices || [];
    },

    toNum(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },

    exportExcel() {
      const from = this.formatDate(this.filters.date_range.startDate);
      const to = this.formatDate(this.filters.date_range.endDate);
      const columns = [
        { header: '#', key: 'seq', width: 6 },
        { header: this.$t('Date'), key: 'date', width: 12 },
        { header: this.$t('Source'), value: r => this.$t(this.sourceLabel(r.source)), width: 16 },
        { header: this.$t('Tax Invoice No.'), key: 'tax_invoice_no', width: 22 },
        { header: this.$t('Document No.'), key: 'document_no', width: 20 },
        { header: this.$t('Supplier'), key: 'supplier_name', width: 28 },
        { header: this.$t('Supplier') + ' ' + this.$t('Tax ID'), key: 'supplier_tax_id', width: 18 },
        { header: this.$t('Branch No'), key: 'supplier_branch', width: 12 },
        { header: this.$t('Taxable'), value: r => this.toNum(r.taxable_amount), width: 14, numFmt: MONEY_FMT },
        { header: this.$t('VAT'), value: r => this.toNum(r.vat_amount), width: 14, numFmt: MONEY_FMT },
        { header: this.$t('Total Amount'), value: r => this.toNum(r.total_amount), width: 16, numFmt: MONEY_FMT },
        { header: this.$t('Claimable'), value: r => (r.is_vat_claimable ? this.$t('Yes') : this.$t('No')), width: 12 },
      ];
      const preRows = [
        [this.$t('VAT Purchase List')],
        [this.selectedBranchLabel + '  |  ' + from + ' — ' + to],
        [
          this.$t('Total Invoices') + ': ' + (this.summary.total_invoices || 0),
          this.$t('Total Taxable') + ': ' + this.toNum(this.summary.total_taxable),
          this.$t('Input VAT') + ': ' + this.toNum(this.summary.total_vat),
          this.$t('Claimable Input VAT') + ': ' + this.toNum(this.summary.total_vat_claimable),
          this.$t('Total Amount') + ': ' + this.toNum(this.summary.total_amount),
        ],
        [],
      ];
      const postRows = [
        [
          this.$t('Total'), '', '', '', '', '', '', '',
          this.toNum(this.summary.total_taxable),
          this.toNum(this.summary.total_vat),
          this.toNum(this.summary.total_amount),
          '',
        ],
      ];
      exportRowsToXlsx({
        filename: `vat-purchase-${from}-${to}.xlsx`,
        sheetName: 'VAT Purchase',
        columns,
        rows: this.invoices,
        preRows,
        postRows,
      });
    },

    async buildPdfDefinition() {
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('VAT Purchase List'), style: 'sectionTitle' },
        {
          columns: [
            { text: [{ text: this.$t('Total Invoices') + '\n', style: 'kpiLabel' }, { text: String(this.summary.total_invoices || 0), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Total Taxable') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.total_taxable), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Input VAT') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.total_vat), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Total Amount') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.total_amount), style: 'kpiValue' }], width: '*' },
          ],
          margin: [0, 0, 0, 16],
        },
        {
          table: {
            headerRows: 1,
            widths: [18, 'auto', 'auto', 'auto', '*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: '#', style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Date'), style: 'tableHeader' },
                { text: this.$t('Source'), style: 'tableHeader' },
                { text: this.$t('Tax Invoice No.'), style: 'tableHeader' },
                { text: this.$t('Supplier'), style: 'tableHeader' },
                { text: this.$t('Taxable'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('VAT'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Total Amount'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.invoices.map(r => {
                const missing = !r.supplier_tax_id;
                const cell = (text, extra = {}) => ({
                  text,
                  style: 'tableCell',
                  color: missing ? '#b45309' : undefined,
                  ...extra,
                });
                const supplier = r.supplier_name
                  + (r.supplier_tax_id ? '  ' + r.supplier_tax_id : '  (' + this.$t('Missing Tax ID') + ')');
                return [
                  cell(String(r.seq), { alignment: 'right' }),
                  cell(r.date),
                  cell(this.$t(this.sourceLabel(r.source))),
                  cell((r.tax_invoice_no || '-') + (r.is_vat_claimable ? '' : '  (' + this.$t('Not claimable') + ')')),
                  cell(supplier),
                  cell(this.formatCurrencyRaw(r.taxable_amount), { alignment: 'right' }),
                  cell(this.formatCurrencyRaw(r.vat_amount), { alignment: 'right' }),
                  cell(this.formatCurrencyRaw(r.total_amount), { alignment: 'right' }),
                ];
              }),
              [
                { text: this.$t('Total'), style: 'tableHeader', colSpan: 5 }, {}, {}, {}, {},
                { text: this.formatCurrencyRaw(this.summary.total_taxable), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.summary.total_vat), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.summary.total_amount), style: 'tableHeader', alignment: 'right' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ];
      return { content };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
.table-warning-subtle {
  background-color: #fff8e6;
}
</style>
